import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box, Button, Checkbox, ClickAwayListener, InputAdornment,
  MenuItem, Paper, Popper, TextField, Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'

// Shared filter-bar building blocks, mirrored from the Network Overview module
// (Beacon4FilterBar) so the Performance dashboard gets the same, more functional
// dropdowns and grouped layout. Kept as a local copy to avoid touching the
// Network Overview module — consolidate into a shared location later if desired.

export function Label({ children }) {
  return (
    <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '3px' }}>
      {children}
    </Typography>
  )
}

// Visual group wrapper — a subtle titled box around a horizontal row of fields.
export function FieldGroup({ title, children }) {
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 0.25,
      border: '1px solid', borderColor: 'divider',
      borderRadius: 1, px: 1, py: 0.5,
      bgcolor: 'rgba(0,0,0,0.015)',
    }}>
      <Typography sx={{
        fontSize: 8.5, fontWeight: 700, color: 'text.disabled',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        lineHeight: 1, mb: -0.25, whiteSpace: 'nowrap',
      }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        {children}
      </Box>
    </Box>
  )
}

// Virtualised list — only renders visible rows (keeps large option lists snappy).
const ITEM_H = 28
const MAX_VISIBLE = 8

const VirtualList = memo(function VirtualList({ items, selectedSet, onToggle }) {
  const [scrollTop, setScrollTop] = useState(0)
  const containerH = Math.min(items.length, MAX_VISIBLE) * ITEM_H

  if (items.length === 0) {
    return <MenuItem disabled sx={{ fontSize: 11 }}>No results</MenuItem>
  }

  const startIdx   = Math.floor(scrollTop / ITEM_H)
  const endIdx     = Math.min(startIdx + MAX_VISIBLE + 1, items.length)
  const visible    = items.slice(startIdx, endIdx)
  const paddingTop = startIdx * ITEM_H

  return (
    <Box
      onScroll={e => setScrollTop(e.currentTarget.scrollTop)}
      sx={{ height: containerH, overflowY: 'auto', position: 'relative', py: 0.5 }}
    >
      <Box sx={{ height: items.length * ITEM_H, position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: paddingTop, left: 0, right: 0 }}>
          {visible.map(item => (
            <Box key={item.key ?? item.value} onClick={() => onToggle(item.value)}
              sx={{ height: ITEM_H, display: 'flex', alignItems: 'center', px: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              <Checkbox checked={selectedSet.has(item.value)} size="small" sx={{ p: 0.5 }} disableRipple />
              <Typography noWrap sx={{ fontSize: 11 }}>{item.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
})

// `options` accepts either plain strings (value === label) or { value, label }
// objects, for cases where the stored identity differs from what's shown/searched.
// `value` may be a comma-joined string OR an array; `onChange` always emits a
// comma-joined string of VALUES.
export const MultiSelectDropdown = memo(function MultiSelectDropdown({ label, value = '', options = [], onChange, disabled, emptyLabel = '(All)' }) {
  const [open, setOpen]     = useState(false)
  const [search, setSearch] = useState('')
  const anchorRef           = useRef(null)
  const onChangeRef         = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  const normalized = useMemo(
    () => options.map(o => (o && typeof o === 'object') ? o : { value: o, label: o }),
    [options]
  )

  const selectedSet = useMemo(
    () => new Set(typeof value === 'string' ? value.split(',').filter(Boolean) : value),
    [value]
  )
  // Options may carry an optional `search` string (extra keywords to match on)
  // so a field can display one token but still be findable by another — e.g. a
  // site shown as its CSR yet searchable by its JVID too.
  const filtered = useMemo(
    () => normalized.filter(o => (o.search ?? o.label).toLowerCase().includes(search.toLowerCase())),
    [normalized, search]
  )

  const toggle = useCallback((val) => {
    const next = new Set(selectedSet)
    next.has(val) ? next.delete(val) : next.add(val)
    onChangeRef.current([...next].join(','))
  }, [selectedSet])

  const handleSelectAll = useCallback(() => { onChangeRef.current(filtered.map(o => o.value).join(',')) }, [filtered])
  const handleClear     = useCallback(() => { onChangeRef.current('') }, [])
  const handleClose     = useCallback(() => { setOpen(false); setSearch('') }, [])
  const selected        = useMemo(() => [...selectedSet], [selectedSet])
  const selectedLabels  = useMemo(
    () => selected.map(val => normalized.find(o => o.value === val)?.label ?? val),
    [selected, normalized]
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {label && <Label>{label}</Label>}
      <Box ref={anchorRef}>
        <Button
          variant="outlined" size="small" disabled={disabled}
          onClick={() => !disabled && setOpen(v => !v)}
          sx={{
            height: 28, fontSize: 11, minWidth: 130, maxWidth: 200,
            textTransform: 'none', justifyContent: 'space-between',
            color: selected.length ? 'text.primary' : 'text.disabled',
            borderColor: selected.length ? 'primary.main' : 'divider',
            bgcolor: selected.length ? 'primary.light' : 'background.default',
            '&:hover': { borderColor: 'primary.main' },
            overflow: 'hidden',
          }}
          endIcon={<Typography sx={{ fontSize: 10, ml: 0.5 }}>▾</Typography>}
        >
          <Typography noWrap sx={{ fontSize: 11, maxWidth: 150 }}>
            {selected.length === 0 ? emptyLabel : selected.length === 1 ? selectedLabels[0] : `${selected.length} selected`}
          </Typography>
        </Button>
      </Box>

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1400 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={4} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', minWidth: 200, maxWidth: 260 }}>
            <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <TextField
                size="small" fullWidth autoFocus placeholder="Search..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: 'text.disabled' }} /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { height: 26, fontSize: 11 } }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Button size="small" sx={{ fontSize: 10, textTransform: 'none', py: 0 }} onClick={handleClear}>Clear</Button>
              <Button size="small" sx={{ fontSize: 10, textTransform: 'none', py: 0 }} onClick={handleSelectAll}>Select all</Button>
            </Box>
            <VirtualList items={filtered} selectedSet={selectedSet} onToggle={toggle} />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  )
})
